import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import { pickLocalized } from "@/lib/i18n/localized";
import type { StoreSubscriptionStatusItem, SubscriptionPlanType } from "@/lib/api/types";
import { useStoreSubscriptionsQuery } from "@/shared/stores/services/queries/useStoreSubscriptionsQuery";
import { useSubscriptionsQuery } from "@/shared/services/useSubscriptionsQuery";
import { StoreSubscriptionActionsMenu } from "@/shared/stores/components/subscriptions/StoreSubscriptionActionsMenu";
import { useTableDensity } from "@/shared/layout/table-density";
import { formatDate } from "@/lib/formatting/date-time";
import {
  AnswerTile,
  Badge,
  Button,
  DataTable,
  ErrorState,
  ItemCell,
  LoadingState,
  SegmentedControl,
  StatusDot,
  TableEmpty,
  TablePagination,
  TileGrid,
  type Column,
  type DotTone,
} from "@/shared/rd";
import { AppliedFilter, PlanFilterMenu, type PlanFilterItem } from "../components/PlanFilterMenu";
import { planTypeLabelKey } from "../lib/plan-display";
import "../styles.css";

/** A zero-priced plan (Free) has no price and no billing period to show. */
const NOT_BILLED = "—";

type AdminStoreSubscriptionsPageClientProps = {
  locale: Locale;
  limit: number;
  offset: number;
  overdue: boolean;
  selectedPlanId: string;
  selectedPlanType: SubscriptionPlanType | "";
};

function formatAmount(
  value: number | null | undefined,
  locale: string,
  currency?: string,
) {
  if (value === null || value === undefined) {
    return "-";
  }
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency || "EGP",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value} ${currency ?? ""}`.trim();
  }
}

export function StoreSubscriptionsPage({
  locale,
  limit,
  offset,
  overdue,
  selectedPlanId,
  selectedPlanType,
}: AdminStoreSubscriptionsPageClientProps) {
  const t = useTranslations();
  const navigate = useNavigate();
  const [compact, , setCompact] = useTableDensity();
  const [filterQuery, setFilterQuery] = useState("");
  const { data, isLoading, error, refetch } = useStoreSubscriptionsQuery({
    limit,
    offset,
    overdue,
    planId: selectedPlanId,
    planType: selectedPlanType,
  });
  const { data: plansData } = useSubscriptionsQuery();

  const pageName = t(keys.admin.storeSubscriptionsTitle).toLocaleLowerCase(locale);

  if (isLoading) {
    return (
      <LoadingState
        title={t(keys.shell.loadingTitle, { page: pageName })}
        sub={t(keys.shell.loadingSub)}
        rows={Math.min(limit, 6)}
      />
    );
  }

  if (error || !data) {
    return (
      <ErrorState
        title={t(keys.shell.errorTitle, { page: pageName })}
        sub={t(keys.shell.errorSub)}
        onRetry={() => void refetch()}
        retryLabel={t(keys.shell.retry)}
      />
    );
  }

  const plans = plansData?.items ?? [];
  const activePlan = plans.find((plan) => plan.id === selectedPlanId);

  // Every link keeps the filters it did not change. A filter change resets to
  // the first page: the filtered set is smaller, so the offset you were on may
  // be past its end.
  const go = (next: {
    planId?: string;
    planType?: SubscriptionPlanType | "";
    overdue?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const query = new URLSearchParams();
    query.set("limit", String(next.limit ?? limit));
    query.set("offset", String(next.offset ?? 0));
    const planId = next.planId ?? selectedPlanId;
    const planType = next.planType ?? selectedPlanType;
    const isOverdue = next.overdue ?? overdue;
    if (planId) query.set("planId", planId);
    if (planType) query.set("planType", planType);
    if (isOverdue) query.set("overdue", "true");
    navigate(`/${locale}/admin/stores/subscriptions?${query.toString()}`);
  };

  const needle = filterQuery.trim().toLowerCase();
  const overdueLabel = t(keys.admin.subsStatusOverdue);
  const filterItems: PlanFilterItem[] = [
    ...(overdue || !overdueLabel.toLowerCase().includes(needle)
      ? []
      : [{ id: "__overdue__", label: overdueLabel, onSelect: () => go({ overdue: true }) }]),
    ...plans
      .map((plan) => ({ plan, label: pickLocalized(locale, plan.nameAr, plan.nameEn) }))
      .filter(({ plan, label }) => plan.id !== selectedPlanId && label.toLowerCase().includes(needle))
      .map(({ plan, label }) => ({ id: plan.id, label, onSelect: () => go({ planId: plan.id }) })),
  ];

  // The first condition that applies wins, so a restricted store reads as
  // restricted rather than as merely overdue.
  const statusOf = (item: StoreSubscriptionStatusItem): { tone: DotTone; key: string } => {
    if (item.tier === "FREE") return { tone: "grey", key: keys.admin.tierFree };
    if (item.tier === "PREMIUM") return { tone: "blue", key: keys.admin.tierPremium };
    if (item.tier === "COMPLIMENTARY") return { tone: "purple", key: keys.admin.tierComplimentary };
    if (item.isRestricted) return { tone: "red", key: keys.admin.subsStatusRestricted };
    if (item.isOverdue) return { tone: "red", key: keys.admin.subsStatusOverdue };
    if (item.isTrialExpired) return { tone: "amber", key: keys.admin.subsStatusTrialExpired };
    if (item.isPartiallyPaid) return { tone: "amber", key: keys.admin.subsStatusPartial };
    if (item.isFreeTrial) return { tone: "blue", key: keys.admin.subsStatusTrial };
    return { tone: "green", key: keys.admin.subsStatusActive };
  };

  const items = data.items;
  // Page-scoped, and labelled as such: the endpoint returns a page, so a
  // figure called "MRR" over the whole book would be a different query.
  const activeCount = items.filter((i) => !i.isOverdue && !i.isRestricted).length;
  const overdueCount = items.filter((i) => i.isOverdue || i.isRestricted).length;
  const pageMrr = items.reduce(
    (sum, i) => sum + (i.periodDays > 0 ? (i.amount * 30) / i.periodDays : 0),
    0,
  );

  const columns: Column<StoreSubscriptionStatusItem>[] = [
    {
      key: "store",
      label: t(keys.common.storeName),
      width: 1.8,
      render: (item) => (
        <ItemCell
          media={
            item.logoUrl ? (
              <img
                src={item.logoUrl}
                alt=""
                width={28}
                height={28}
                style={{ borderRadius: "var(--rd-radius-md)", objectFit: "cover", flex: "none" }}
              />
            ) : (
              <Badge id="package" size={28} shape="rounded" />
            )
          }
          name={item.storeName}
        />
      ),
    },
    {
      key: "status",
      label: t(keys.areas.colStatus),
      width: 1.3,
      render: (item) => {
        const status = statusOf(item);
        return <StatusDot tone={status.tone}>{t(status.key)}</StatusDot>;
      },
    },
    {
      key: "plan",
      label: t(keys.admin.storesColPlan),
      width: 1.5,
      render: (item) => (
        <span
          dir="auto"
          style={{ color: "var(--rd-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}
        >
          {pickLocalized(locale, item.planNameAr, item.planNameEn)}
        </span>
      ),
    },
    {
      key: "amount",
      label: t(keys.admin.subsColAmount),
      width: 0.9,
      align: "end",
      numeric: true,
      render: (item) => (item.amount === 0 ? NOT_BILLED : formatAmount(item.amount, locale, item.currency)),
    },
    {
      key: "period",
      label: t(keys.admin.subsColPeriod),
      width: 0.8,
      align: "end",
      numeric: true,
      render: (item) =>
        item.amount === 0 || item.periodDays <= 0
          ? NOT_BILLED
          : t(keys.admin.subsPeriodDays, { days: item.periodDays }),
    },
    {
      key: "next",
      label: t(keys.admin.subsColNext),
      width: 1.1,
      align: "end",
      numeric: true,
      // The date is the thing that is late, so it carries the colour.
      render: (item) => {
        const late = item.isOverdue || item.isRestricted;
        return (
          <span style={{ color: late ? "var(--rd-red)" : "inherit", fontWeight: late ? 500 : 400 }}>
            {formatDate(item.nextPaymentDate, locale)}
          </span>
        );
      },
    },
    {
      key: "last",
      label: t(keys.admin.subsColLast),
      width: 1.1,
      align: "end",
      numeric: true,
      render: (item) => (
        <span style={{ color: "var(--rd-text-secondary)" }}>{formatDate(item.lastPaymentAt, locale)}</span>
      ),
    },
    {
      key: "actions",
      label: "",
      width: "56px",
      align: "end",
      render: (item) => (
        <span style={{ display: "flex", justifyContent: "flex-end" }} onClick={(event) => event.stopPropagation()}>
          <StoreSubscriptionActionsMenu
            storeId={item.storeId}
            locale={locale}
            isFreeTrial={item.isFreeTrial}
            isPremium={item.isPremium}
            planType={item.type}
            tier={item.tier}
          />
        </span>
      ),
    },
  ];

  return (
    <>
      <TileGrid min={250}>
        <AnswerTile
          badge="chart"
          label={t(keys.admin.subsStatMrr)}
          answer={<span className="rd-num">{formatAmount(Math.round(pageMrr), locale, items[0]?.currency)}</span>}
          sub={t("admin.subsStatMrrSub")}
        />
        <AnswerTile
          badge="register"
          label={t(keys.admin.subsStatActive)}
          answer={t("admin.subsStoresCount", { count: activeCount })}
          sub={t("admin.subsStatActiveSub")}
          tone="good"
        />
        <AnswerTile
          badge="clock"
          label={t(keys.admin.subsStatExpiring)}
          answer={t("admin.subsStoresCount", { count: overdueCount })}
          sub={t(overdue ? "admin.subsStatOverdueSubClear" : "admin.subsStatOverdueSub")}
          tone="bad"
          onClick={() => go({ overdue: !overdue })}
        />
      </TileGrid>

      <div className="rd-toolbar">
        <PlanFilterMenu
          label={t(keys.common.filterAdd)}
          searchPlaceholder={t(keys.common.filterSearch)}
          emptyLabel={t(keys.common.noResults)}
          items={filterItems}
          query={filterQuery}
          onQueryChange={setFilterQuery}
        />
        {overdue ? (
          <AppliedFilter
            text={`${t(keys.areas.colStatus)} ${t(keys.common.filterIs)} ${overdueLabel}`}
            removeLabel={t(keys.common.filterRemove)}
            onRemove={() => go({ overdue: false })}
          />
        ) : null}
        {activePlan ? (
          <AppliedFilter
            text={`${t(keys.admin.storesColPlan)} ${t(keys.common.filterIs)} ${pickLocalized(locale, activePlan.nameAr, activePlan.nameEn)}`}
            removeLabel={t(keys.common.filterRemove)}
            onRemove={() => go({ planId: "" })}
          />
        ) : null}
        {selectedPlanType ? (
          <AppliedFilter
            text={`${t(keys.admin.storeSubscriptionsFilterPlanType)} ${t(keys.common.filterIs)} ${t(planTypeLabelKey(selectedPlanType))}`}
            removeLabel={t(keys.common.filterRemove)}
            onRemove={() => go({ planType: "" })}
          />
        ) : null}
        <span style={{ fontSize: 13, color: "var(--rd-text-tertiary)" }}>
          {t("admin.subsStoresCount", { count: data.total })}
        </span>
        <div className="rd-spacer" />
        <SegmentedControl
          label={t("admin.subsDensity")}
          value={compact ? "compact" : "comfortable"}
          onChange={(value) => setCompact(value === "compact")}
          options={[
            { value: "comfortable", label: t(keys.common.densityComfortable) },
            { value: "compact", label: t(keys.common.densityCompact) },
          ]}
        />
      </div>

      <DataTable
        label={t(keys.admin.storeSubscriptionsTitle)}
        className={compact ? "subs-table-compact" : undefined}
        columns={columns}
        rows={items}
        rowKey={(item) => item.storeId}
        minWidth={900}
        empty={
          overdue || selectedPlanId || selectedPlanType ? (
            <TableEmpty
              illustration="admin-no-results"
              title={t(keys.admin.storeSubscriptionsEmptySubtitle)}
              actions={
                <Button
                  variant="secondary"
                  size="sm"
                  icon={X}
                  onClick={() => go({ planId: "", planType: "", overdue: false })}
                >
                  {t(keys.common.clearFilters)}
                </Button>
              }
            >
              {t("admin.storeSubscriptionsFilteredEmptySub")}
            </TableEmpty>
          ) : (
            <TableEmpty illustration="admin-empty-table" title={t("admin.storeSubscriptionsEmptyTitle")}>
              {t("admin.storeSubscriptionsEmptySub")}
            </TableEmpty>
          )
        }
        footer={
          <TablePagination
            locale={locale}
            total={data.total}
            page={Math.floor(offset / limit) + 1}
            pageSize={limit}
            onPage={(page) => go({ offset: (page - 1) * limit })}
            onPageSize={(size) => go({ limit: size, offset: 0 })}
            labels={{
              range: (from, to, total) => t(keys.common.paginationSummary, { from, to, total }),
              rows: t("admin.subsRows"),
              rowsPerPage: t("admin.subsRowsPerPage"),
              previous: t(keys.common.previous),
              next: t(keys.common.next),
              pages: t(keys.common.paginationNav),
            }}
          />
        }
      />
    </>
  );
}
